// Typed client for the account, team, token and analytics settings endpoints
// (the U3/U4 settings UIs bind to these). No UI here, just the typed surface.
import { apiDelete, apiGet, apiPatch, apiPost } from './api';

export interface Profile {
  id: string;
  email: string;
  display_name: string;
  tenant_id: string;
}

export interface Team {
  id: string;
  name: string;
}

export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  created_at: string | null;
  last_used_at: string | null;
  revoked: boolean;
}

/** Returned only from createToken; `token` is the secret, shown once. */
export interface CreatedToken extends ApiToken {
  token: string;
}

export interface GameOverview {
  id: string;
  slug: string;
  name: string;
  player_count: number;
  match_count: number;
}

export interface PlayerSummary {
  id: string;
  handle: string;
  display_name: string | null;
  created_at: string | null;
  last_seen_at: string | null;
}

/** One connected game (the Cognito player bridge, GET /players/links). */
export interface LinkedGame {
  game_id: string;
  slug: string;
  name: string;
  linked_at: string | null;
}

/**
 * `winner`/`raiders_score`/`wardens_score` are Sunder's own faction names,
 * not a genre-neutral shape - today's `/analytics/games/:id/matches` only
 * ever has rows for a project reporting through Sunder's own realtime
 * server, so they're reliably present in practice, but nothing about this
 * type says a future non-Sunder multiplayer integration must shape its
 * match data this way. A reader (`ScreenProjectPlayers`,
 * screens-project-ops.tsx) must gate on the project's own Game Setup `type`
 * being `'Versus'` before treating these fields as meaningful, rather than
 * assuming every match row is a 5v5 raiders-vs-wardens result.
 */
export interface MatchSummary {
  id: string;
  external_id: string;
  map_id: string | null;
  winner: string | null;
  raiders_score: number;
  wardens_score: number;
  started_at: string | null;
  ended_at: string | null;
}

export function getProfile(): Promise<Profile> {
  return apiGet('/me');
}

export function updateProfile(displayName: string): Promise<Profile> {
  return apiPatch('/me/profile', { display_name: displayName });
}

export function getTeam(): Promise<Team> {
  return apiGet('/me/team');
}

export function updateTeam(name: string): Promise<Team> {
  return apiPatch('/me/team', { name });
}

export function deleteAccount(): Promise<{ status: string }> {
  return apiDelete('/me/account');
}

export function createToken(name: string): Promise<CreatedToken> {
  return apiPost('/me/tokens', { name });
}

export function listTokens(): Promise<ApiToken[]> {
  return apiGet('/me/tokens');
}

export function revokeToken(id: string): Promise<{ status: string }> {
  return apiDelete(`/me/tokens/${id}`);
}

export function listLinkedGames(): Promise<LinkedGame[]> {
  return apiGet('/players/links');
}

export function unlinkGame(gameId: string): Promise<{ status: string }> {
  return apiDelete(`/players/link/${gameId}`);
}

export function analyticsGames(): Promise<GameOverview[]> {
  return apiGet('/analytics/games');
}

export function gamePlayers(gameId: string): Promise<PlayerSummary[]> {
  return apiGet(`/analytics/games/${gameId}/players`);
}

export function gameMatches(gameId: string): Promise<MatchSummary[]> {
  return apiGet(`/analytics/games/${gameId}/matches`);
}
